import type {
  ComponentPropsWithoutRef,
  MouseEvent,
  MouseEventHandler,
  Ref,
} from 'react';

import type { FieldLabelBase } from '../FieldLabel';

/** The masking types supported by the `Input` component. */
export type InputMask = 'zip' | 'phone';

/** Configuration for the inline submit affordance rendered inside an `Input`. */
export interface InputSubmitButton {
  /** Visible button text. Used as the accessible name. */
  label: string;
  /** Click handler. Not invoked while the button is disabled. */
  onClick: MouseEventHandler<HTMLButtonElement>;
  /**
   * Render the button as disabled. The button is also disabled automatically
   * when the parent `Input` is `disabled`, so to indicate an in-flight submit
   * set `disabled` on the `Input` itself.
   */
  disabled?: boolean;
  /**
   * The native button type.
   *
   * @defaultValue 'submit'
   */
  type?: 'submit' | 'button';
}

/** A function to mask the value of the input considering the cursor position. */
export type InputMaskFn = (
  value: string | number,
  cursorPosition?: number
) => [maskedValue: string, cursorPosition: number];

/** A function to clean-up the mask when the backspace is pressed. */
export type InputCleanMaskFn = (value: string) => string;

/** The props for the {@link Input} component. */
export interface InputBaseProps
  extends FieldLabelBase, ComponentPropsWithoutRef<'input'> {
  /**
   * Either a string or a number, as opposed to native `InputHTMLElement` which
   * accepts also `string[]`.
   */
  value?: string | number | undefined;
  /** Toggle the invalid state. */
  invalid?: boolean;
  /** Toggle the highlighted state */
  highlighted?: boolean;
  /** Visually hide the input label while keeping it in the accessibility tree. */
  hideLabel?: boolean;
  /** When `type="password"`, show or hide the button to reveal the password. */
  withRevealButton?: boolean;
  /** Call a function when RevealButton is clicked. */
  onRevealButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Toggle the spin buttons to increase/decrease the numeric value. Works with
   * `min`, `max`, etc.
   */
  withSpinButtons?: boolean;
  /**
   * Render an inline submit affordance inside the input's outline. Designed for
   * the foundation theme; use for short single-field forms (e.g. an email
   * capture).
   */
  submitButton?: InputSubmitButton;
}

/**
 * A tuple representing a custom mask to use with the {@link Input} component.
 *
 * Each mask is represented by a function to mask the value and a function to
 * cleanup the mask when the backspace is pressed. Since the mask alters the
 * content of the field, the mask function must return the updated cursor
 * position.
 *
 * ### Example
 *
 * ```ts
 * const clean: InputCleanMaskFn = value => {
 *   return value.replace(/[^0-9]/g, '');
 * };
 * const mask: InputMaskFn = (value, cursorPosition) => {
 *   const amount = String(value);
 *   const maskedValue = amount
 *     ? `${amount} bag${amount !== '1' ? 's' : ''}`
 *     : '';
 *   const newCursor = Math.min(cursorPosition, clean(amount).length);
 *   return [maskedValue, newCursor];
 * };
 * const customMask: InputCustomMask = [mask, clean];
 * ```
 */
export type InputCustomMask = [InputMaskFn, InputCleanMaskFn];

/** The props for {@link Input} when a mask is applied. */
export interface InputWithMask extends InputBaseProps {
  /** Input with mask is available only for these types. */
  type: 'email' | 'tel' | 'text' | 'url' | undefined;
  /**
   * The mask to apply. Can be either one of the {@link InputMask} or a tuple
   * with a mask and clean mask functions.
   */
  mask: InputMask | InputCustomMask;
  ref?: Ref<HTMLInputElement>; // Need to add this to make the ref work in the type union (not sure why)
}

/** The props of the {@link Input} component. */
export type InputProps = InputBaseProps | InputWithMask;

export function isMaskedInput(props: InputProps): props is InputWithMask {
  return (
    props.type !== 'number' &&
    props.type !== 'date' &&
    props.type !== 'password' &&
    'mask' in props &&
    Boolean(props.mask)
  );
}
